import { Button, Input, Select, type SelectOptionType, Switch } from '@bp/ui-components';
import { graphql } from 'client/gql';
import type { CalendarEventCreateInput } from 'client/gql/graphql';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import i18next from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { startEvent } from 'utils/eventHelper';
import { v4 } from 'uuid';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useDays } from '../../../hooks/useDays';
import { useGroupMembersCount } from '../../../hooks/useGroupMembersCount';
import { backendApi } from '../../../utils/backendApi';
import { connectByUuid } from '../../../utils/connectLib';
import { showErrorToast } from '../../../utils/showErrorToast';
import styles from './InstantMeetings.module.scss';
import { startAndJoinMeeting } from './startAndJoinMeeting';

const CreateCalendarEventMutationDocument = graphql(`
  mutation BpCreateCalendarEvent($input: [CalendarEventCreateInput!]!) {
    createCalendarEvents(input: $input) {
      calendarEvents {
        uuid
        __typename
        virtualLocations {
          uuid
          __typename
          token
        }
      }
    }
  }
`);

const InstantMeetingsFormPageQueryDocument = graphql(`
  query InstantMeetingsFormPageData($groupUuid: ID!) {
    groupsForLicenses: groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      organization {
        uuid
        __typename
        licensedProducts {
          __typename
          ... on BigBlueButton {
            uuid
            allowRecording
          }
          ... on Bildungsplattform {
            uuid
          }
          ... on Nextcloud {
            uuid
          }
          ... on Threema {
            uuid
          }
          ... on ThreemaBroadcast {
            uuid
          }
          ... on Zoom {
            uuid
          }
        }
      }
    }
    ...UseGroupMembersCountFields
  }
`);

export const DEFAULT_MEETING_DURATION: number = 90; // minutes

export type InstantMeeting = {
  participantsCount?: number;
  duration: number;
  record: boolean;
  service: string;
  join?: boolean;
};

export function InstantMeetingsForm({ handleClose, groupUuid }: { handleClose: () => void; groupUuid: string }) {
  const [{ data }, refetch] = useQuery({
    query: InstantMeetingsFormPageQueryDocument,
    variables: {
      groupUuid,
    },
  });
  const availableServices = data?.groupsForLicenses[0]?.organization?.licensedProducts?.map((s) => {
    return {
      service: s.__typename,
      allowRecording: (s.__typename === 'BigBlueButton' && s.allowRecording) ?? false,
    };
  });
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { today } = useDays();
  const [, bpCreateEvents] = useMutation(CreateCalendarEventMutationDocument);

  const { groupMembers, organizationUuid } = useGroupMembersCount(data);

  async function handleSubmit(values: InstantMeeting) {
    let canSave = !values.service.includes('bbb');

    if (values.service.includes('bbb')) {
      const attendees = values.participantsCount ?? 0;
      const tempUuid = v4();
      const testResponse: { [tempUuid: string]: number }[] = await backendApi.testForSufficientBBBSlots({
        id: tempUuid,
        maxAttendees: attendees,
        start: dayjs().toISOString(),
        end: dayjs()
          .add(dayjs.duration(values.duration ? values.duration : DEFAULT_MEETING_DURATION, 'minutes'))
          .toISOString(),
        organization: organizationUuid, // the group's organization -> important if collaboration
      });

      const slots = testResponse[0]?.[tempUuid] ?? 0;
      if (slots > 0) canSave = true;

      if (!canSave) {
        showErrorToast({
          message: i18next.t('meetings.tooManyParticipantsWithNumber', {
            number: attendees + slots,
          }),
          name: '',
          graphQLErrors: [],
        });
      }
    }

    if (canSave) {
      const input: CalendarEventCreateInput = {
        foreignRefId: [],
        title: t('meetings.instantMeeting'),
        organization: connectByUuid(organizationUuid),
        holder: { Group: connectByUuid(groupUuid) },
        start: dayjs().toISOString(),
        duration: ((values.duration ?? DEFAULT_MEETING_DURATION) * 60).toString(),
        days: { connect: [{ where: { node: { uuid: today?.uuid } } }] },
        owner: connectByUuid(pimAuthClaims.getProfile().uuid ?? ''),
        maxAttendees: values.participantsCount ?? 1,
        recordVirtualEvent: values.record ?? false,
        categories: values.service ? ['virtual', values.service] : undefined, // 'virtual' MUSS hier mit bei, das backend prüft danach
      };

      const { error, data } = await bpCreateEvents({ input });

      if (error) {
        showErrorToast(error);
      } else {
        let success: boolean = false;

        if (!values.join) {
          success = !!(
            data?.createCalendarEvents?.calendarEvents?.[0] &&
            (await startEvent(data.createCalendarEvents.calendarEvents[0]?.virtualLocations[0]?.token))
          );
        }

        if (values.join) {
          success = !!(
            data?.createCalendarEvents?.calendarEvents?.[0] &&
            (await startAndJoinMeeting(
              data.createCalendarEvents.calendarEvents[0]?.virtualLocations[0]?.token,
              pimAuthClaims,
              groupUuid,
            ))
          );
        }

        handleClose();
        refetch();

        if (!success)
          showErrorToast({
            message: i18next.t('meetings.noVirtualLocation'),
            name: '',
            graphQLErrors: [],
          });
      }
    }
  }

  function getServiceOptions() {
    const services = [];
    if (availableServices?.find((s) => s.service === 'BigBlueButton'))
      services.push({ value: 'bbb', label: 'BigBlueButton' });
    if (availableServices?.find((s) => s.service === 'Zoom')) services.push({ value: 'zoom', label: 'Zoom' });
    return services;
  }

  const initialValues: InstantMeeting = {
    record: false,
    join: false,
    service: '',
    duration: DEFAULT_MEETING_DURATION,
    participantsCount: groupMembers + 10,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {
        // no-op
      }}
    >
      {(formik) => (
        <Form>
          <div className={styles.wrapper}>
            <Select
              name='service'
              options={getServiceOptions()}
              placeholder='Service auswählen'
              required={true}
              label='Über welchen Service wollen Sie die Besprechung starten?'
              onChange={(opt) => formik.setFieldValue('service', (opt as SelectOptionType).value)}
              error={formik.errors.service}
              onBlur={formik.handleBlur}
            />
            {formik.values.service && (
              <>
                <hr style={{ width: '100%' }} />
                <div className={styles.flex}>
                  <Input
                    name='participantsCount'
                    placeholder={t('meetings.participantsCount.placeholder', {
                      count: '100',
                    })}
                    label={t('meetings.participantsCount.label')}
                    onChange={(ev) => formik.setFieldValue('participantsCount', +ev.target.value)}
                    onBlur={formik.handleBlur}
                    value={formik.values.participantsCount}
                    error={formik.errors.participantsCount}
                  />
                  <Input
                    name='duration'
                    placeholder='in Minuten'
                    label={t('meetings.expectedDuration')}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.duration?.toString() as string}
                    error={formik.errors.duration}
                  />
                </div>

                {availableServices?.find((s) => s.service === formik.values.service)?.allowRecording && (
                  <div className={`${styles.flex} ${styles['no-column']}`}>
                    <div>{t('meetings.record')}</div>
                    <Switch size='large' name='record' onChange={formik.handleChange} />
                  </div>
                )}
                <div>
                  <Button
                    hierarchy='primary'
                    fullWidth={true}
                    onClick={async () => {
                      await handleSubmit(formik.values);
                      formik.resetForm();
                    }}
                  >
                    {t('meetings.start')}
                  </Button>
                  <Button
                    hierarchy='primary'
                    fullWidth={true}
                    onClick={async () => {
                      //  formik.setFieldValue('join', true)  would always be 1 Tick behind, so I went for the hacky solution
                      await handleSubmit({ ...formik.values, join: true });
                      formik.resetForm();
                    }}
                  >
                    {t('meetings.startAndJoin')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
